// frontend/src/pages/MatchScoring/InningsBreakScreen.js
import React, { useState } from 'react';
import api from '../../utils/api';

export function InningsBreakScreen({ matchId, completedInnings, onStartNext }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState(null);

  const total  = completedInnings.total_runs;
  const wkts   = completedInnings.wickets;
  const balls  = completedInnings.legal_balls;
  const ov     = `${Math.floor(balls / 6)}.${balls % 6}`;
  const target = total + 1;

  async function go() {
    setBusy(true); setErr(null);
    try {
      // Make sure the just-completed innings is marked completed.
      // The /innings/end endpoint is idempotent.
      await api.post(`/scorer/matches/${matchId}/innings/${completedInnings.id}/end`, {
        end_reason: completedInnings.end_reason || 'overs_done',
      }).catch(() => {});
      onStartNext(target);
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'Failed to advance');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="sc-stage">
      <div className="sc-banner">
        <div className="sc-banner-title">Innings 1 ended</div>
        <div className="sc-banner-body">
          {total}/{wkts} in {ov} overs · target for innings 2 is <strong>{target}</strong>
        </div>
      </div>

      {err && <div className="sc-error-box">{err}</div>}

      <button className="sc-stage-cta" disabled={busy} onClick={go}>
        {busy ? 'Loading…' : '▶ Start innings 2'}
      </button>
    </div>
  );
}


export function MatchEndScreen({ allInnings, matchId, onComplete, playersPerSide }) {
  const [mom, setMom]   = useState(null);   // set by the server: top points-scorer
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState(null);

  // Determine result
  const inn1 = allInnings.find(i => i.innings_number === 1);
  const inn2 = allInnings.find(i => i.innings_number === 2);
  let resultLine = '—';
  if (inn1 && inn2) {
    const t1 = parseInt(inn1.total_runs, 10);
    const t2 = parseInt(inn2.total_runs, 10);
    if (t2 > t1) {
      // UCC plays last-man-batting: the chasing side has players_per_side
      // wickets to lose, not ten. Same rule as recordLiveResult() on the
      // server, so this line and the recorded result never disagree.
      const squad = parseInt(playersPerSide, 10) || 11;
      resultLine = `Side ${inn2.batting_side} won by ${Math.max(0, squad - parseInt(inn2.wickets, 10))} wickets`;
    } else if (t2 < t1) {
      resultLine = `Side ${inn1.batting_side} won by ${t1 - t2} runs`;
    } else {
      resultLine = 'Match tied';
    }
  }

  async function finish() {
    setBusy(true); setErr(null);
    try {
      // Re-derives figures and points, records the result, and sets Man of
      // the Match to the top points-scorer (UCC rule) — all server-side.
      const r = await api.post(`/scorer/matches/${matchId}/result-from-play`, {});
      const m = r.data?.mom;
      setMom(m?.name ? m : { skipped: m?.skipped || 'not set' });
      if (m?.name) onComplete?.(m.name);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Finalise failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="sc-result">
        <div className="sc-result-line">{resultLine}</div>
        <div className="sc-result-detail">Match complete</div>
      </div>
      <div className="sc-stage">
        <h2>Man of the match</h2>
        <div style={{ fontSize: 13, color: 'var(--sc-text-mute)', marginBottom: 10 }}>
          {mom?.name
            ? <>⭐ <strong>{mom.name}</strong> — {mom.points} pts{mom.joint ? ' (joint)' : ''}</>
            : mom?.skipped
              ? <>Not set automatically: {mom.skipped}. An admin can set it.</>
              : <>Set automatically when you finalise — the top points-scorer.</>}
        </div>

        {err && <div className="sc-error-box">{err}</div>}

        {mom?.skipped
          ? <button className="sc-stage-cta" onClick={() => onComplete?.(null)}>Continue to scorecard</button>
          : <button className="sc-stage-cta" disabled={busy} onClick={finish}>
              {busy ? 'Finalising…' : 'Finalise scorecard'}
            </button>}
      </div>
    </>
  );
}
